"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { onAuthStateChanged } from "firebase/auth";
import { collection, query, orderBy, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, Timestamp } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { auth, db, storage } from "@/lib/firebase";
import { Edit2, Trash2, Plus, Save, X, Loader2, Database, Image as ImageIcon, ArrowUp, ArrowDown } from "lucide-react";

const ADMIN_UID = "EkIixz8no1Ouv5KPwIrQHw9ksKo1";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: "General" | "Smartwatch" | "Padel" | "Tennis" | "Badminton";
  order: number;
  timestamp?: Timestamp;
  imageUrl?: string;
  imagePath?: string;
}

const DEFAULT_FAQS: Omit<FAQItem, "id">[] = [
  {
    category: "General",
    question: "What does MVP stands for?",
    answer: "In sports MVP stands for Most Valuable Player. Are you ready to become one?",
    order: 100
  },
  {
    category: "General",
    question: "Can I track my stats over time?",
    answer: "Absolutely! All your matches are saved to your profile. In near future, you can view detailed analytics, where and with whom you played, including win rates and other statistics over time.",
    order: 200
  },
  {
    category: "Smartwatch",
    question: "The connection to my Garmin isn't working. What can I do?",
    answer: "First and foremost, open Garmin Connect on your mobile phone and check if you are indeed connected to your Garmin watch. Specifically for iPhone, you must first pair your Garmin smartwatch using the ⌚Smartwatch⌚ link on the home screen of the MVPlayer APP. If you are connected but it still doesn't work, try one or more of the following steps.\n\n1. Start and stop the Bluetooth connection on your phone\n2. Turn your Garmin watch completely off and then restart it\n3. Turn your mobile phone completely off and then restart it\n\nAfter each step, start a new game on your mobile phone and check the connection to your Garmin. Normally, this should completely resolve the problem.",
    order: 250
  },
  {
    category: "Padel",
    question: "Does the app support Golden Point?",
    answer: "Yes, MVPlayer fully supports Golden Point scoring, which is the standard for professional Padel matches.",
    order: 300
  },
  {
    category: "Padel",
    question: "How do I score a match with 3 sets?",
    answer: "Simply start a new game session and the app will guide you through set-by-set scoring until a winner is determined.",
    order: 400
  },
  {
    category: "Tennis",
    question: "Does it support tie-breaks?",
    answer: "Yes, MVPlayer supports standard tie-breaks at 6-6 and super tie-breaks for the final set if selected in settings.",
    order: 500
  },
  {
    category: "Tennis",
    question: "Can I use it for singles and doubles?",
    answer: "Absolutely! Both singles and doubles formats are fully supported with their respective scoring rules.",
    order: 600
  },
  {
    category: "Badminton",
    question: "What is the scoring system?",
    answer: "MVPlayer uses the standard rally point system where each rally results in a point, typically played to 21.",
    order: 700
  },
  {
    category: "Badminton",
    question: "Is it optimized for high-intensity play?",
    answer: "Yes, the interface is designed for lightning-fast score updates between rallies, so you can focus on your game.",
    order: 800
  }
];

export default function FAQPage() {

  const [isAdmin, setIsAdmin] = useState(false);

  const [loading, setLoading] = useState(true);

  const [isSaving, setIsSaving] = useState(false);

  const [faqs, setFaqs] = useState<FAQItem[]>([]);

  

  // Edit State

  const [editingId, setEditingId] = useState<string | null>(null);

  const [editForm, setEditForm] = useState<Partial<FAQItem>>({});

  const [editFile, setEditFile] = useState<File | null>(null);



  // Insert State

  const [insertingAfterId, setInsertingAfterId] = useState<string | null>(null);

  const [addForm, setAddForm] = useState<Partial<FAQItem>>({});

  const [addFile, setAddFile] = useState<File | null>(null);



  useEffect(() => {

    const unsubscribe = onAuthStateChanged(auth, (user) => {

      setIsAdmin(user?.uid === ADMIN_UID);

    });

    return () => unsubscribe();

  }, []);



  useEffect(() => {

    fetchFaqs();

  }, []);



  useEffect(() => {
    const handleHashScroll = () => {
      if (typeof window !== "undefined" && window.location.hash) {
        const id = window.location.hash.replace("#", "");
        const element = document.getElementById(id);
        if (element) {
          // Use a small delay to ensure content is rendered and layout is stable
          setTimeout(() => {
            element.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }
      }
    };

    if (!loading) {
      handleHashScroll();
    }
  }, [loading]);

  const scrollToCategory = (e: React.MouseEvent<HTMLAnchorElement>, cat: string) => {
    e.preventDefault();
    const id = cat.toLowerCase();
    const element = document.getElementById(id);
    if (element) {
      // Update hash without jumping
      window.history.pushState(null, "", `#${id}`);
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fetchFaqs = async () => {

    try {

      const q = query(collection(db, "dbMVPlayer", "dbFaq", "items")); 

      const snapshot = await getDocs(q);

      const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as FAQItem));

      

      items.sort((a, b) => (a.order || 0) - (b.order || 0));

      

      setFaqs(items);

      setLoading(false);

    } catch (error) {

      console.error("Error fetching FAQs:", error);

      setLoading(false);

    }

  };



  const handleSeedDefaults = async () => {

    if (!confirm("This will add all default FAQs to the database. Continue?")) return;

    setLoading(true);

    try {

      const promises = DEFAULT_FAQS.map(item => 

        addDoc(collection(db, "dbMVPlayer", "dbFaq", "items"), {

          ...item,

          timestamp: serverTimestamp()

        })

      );

      await Promise.all(promises);

      await fetchFaqs();

    } catch (error) {

      console.error("Error seeding defaults:", error);

      alert("Failed to seed defaults");

    } finally {

      setLoading(false);

    }

  };



    const uploadImage = async (file: File) => {



      try {



        console.log("Starting image upload...", file.name);



        const storageRef = ref(storage, `faq/${Date.now()}_${file.name}`);



        console.log("Storage Ref:", storageRef.fullPath, "Bucket:", storageRef.bucket);



        



        const snapshot = await uploadBytes(storageRef, file);



        console.log("Upload completed", snapshot);



        



        const url = await getDownloadURL(storageRef);



        console.log("Download URL:", url);



        



        return {



          url: url,



          path: storageRef.fullPath



        };



      } catch (error) {



        console.error("Error in uploadImage:", error);



        throw error;



      }



    };



  const startInsertAfter = (item: FAQItem) => {

    setInsertingAfterId(item.id);

    setAddForm({ category: item.category, question: "", answer: "" });

    setAddFile(null);

  };



  const handleInsertSubmit = async (currentItem: FAQItem) => {

    if (!addForm.question || !addForm.answer) {

      alert("Please fill in question and answer");

      return;

    }



    setIsSaving(true);

    const catFaqs = faqs.filter(f => f.category === currentItem.category);

    const currentIndex = catFaqs.findIndex(f => f.id === currentItem.id);

    const nextItem = catFaqs[currentIndex + 1];



    let newOrder;

    if (nextItem) {

       newOrder = (currentItem.order + nextItem.order) / 2;

    } else {

       newOrder = currentItem.order + 100;

    }



    try {

      let imageData = {};

      if (addFile) {

        const { url, path } = await uploadImage(addFile);

        imageData = { imageUrl: url, imagePath: path };

      }



      await addDoc(collection(db, "dbMVPlayer", "dbFaq", "items"), {

        category: currentItem.category,

        question: addForm.question,

        answer: addForm.answer,

        order: newOrder,

        timestamp: serverTimestamp(),

        ...imageData

      });

      setInsertingAfterId(null);

      setAddForm({});

      setAddFile(null);

      fetchFaqs();

    } catch (error) {

      console.error("Error adding FAQ:", error);

      alert("Failed to add FAQ: " + error);

    } finally {

      setIsSaving(false);

    }

  };



  const handleUpdate = async (id: string, currentItem: FAQItem) => {

    setIsSaving(true);

    try {

      let imageData = {};

      

      // If a new file is selected

      if (editFile) {

        // Delete old image if exists

        if (currentItem.imagePath) {

          try {

            await deleteObject(ref(storage, currentItem.imagePath));

          } catch (e) {

            console.error("Error deleting old image:", e);

          }

        }

        const { url, path } = await uploadImage(editFile);

        imageData = { imageUrl: url, imagePath: path };

      } else if (editForm.imageUrl === null) {

         // Explicit removal

         if (currentItem.imagePath) {

            try {

               await deleteObject(ref(storage, currentItem.imagePath));

            } catch (e) {

               console.error("Error deleting old image:", e);

            }

         }

         imageData = { imageUrl: null, imagePath: null };

      }



      await updateDoc(doc(db, "dbMVPlayer", "dbFaq", "items", id), {

        ...editForm,

        ...imageData

      });

      setEditingId(null);

      setEditForm({});

      setEditFile(null);

      fetchFaqs();

    } catch (error) {

      console.error("Error updating FAQ:", error);

      alert("Failed to update FAQ: " + error);

    } finally {

      setIsSaving(false);

    }

  };



    const handleDelete = async (item: FAQItem) => {



      // Check if it's the first item in its category



      const catFaqs = faqs.filter(f => f.category === item.category);



      if (catFaqs.length > 0 && catFaqs[0].id === item.id) {



        alert("Cannot delete the first Q&A for a category.");



        return;



      }



  



      if (!confirm("Are you sure you want to delete this FAQ?")) return;



      setIsSaving(true);

    try {

      if (item.imagePath) {

        try {

          await deleteObject(ref(storage, item.imagePath));

        } catch (e) {

          console.error("Error deleting image:", e);

        }

      }

      await deleteDoc(doc(db, "dbMVPlayer", "dbFaq", "items", item.id));

      setFaqs(prev => prev.filter(f => f.id !== item.id));

    } catch (error) {

      console.error("Error deleting FAQ:", error);

      alert("Failed to delete FAQ");

    } finally {

      setIsSaving(false);

    }

  };



  const handleMove = async (item: FAQItem, direction: 'up' | 'down') => {
    setIsSaving(true);
    try {
      // 1. Get all FAQs for this category, sorted by order
      const catFaqs = faqs
        .filter(f => f.category === item.category)
        .sort((a, b) => (a.order || 0) - (b.order || 0));
        
      const currentIndex = catFaqs.findIndex(f => f.id === item.id);
      
      if (currentIndex === -1) return;

      let targetItem: FAQItem | undefined;

      if (direction === 'up' && currentIndex > 0) {
        targetItem = catFaqs[currentIndex - 1];
      } else if (direction === 'down' && currentIndex < catFaqs.length - 1) {
        targetItem = catFaqs[currentIndex + 1];
      }

      if (targetItem) {
        const itemOrder = item.order || 0;
        const targetOrder = targetItem.order || 0;

        // If orders are the same, we need to artificially separate them
        if (Math.abs(itemOrder - targetOrder) < 0.001) {
             // If we want to swap them but they are equal, we effectively just need to change one of them
             // to be different.
             // Moving UP: item should have smaller order than target.
             // Moving DOWN: item should have larger order than target.
             
             let newOrderForItem = targetOrder;
             let newOrderForTarget = itemOrder;

             // Since they are equal, we force a difference
             if (direction === 'up') {
                newOrderForItem = targetOrder - 1; 
                newOrderForTarget = targetOrder + 1;
             } else {
                newOrderForItem = targetOrder + 1;
                newOrderForTarget = targetOrder - 1;
             }
             
             await updateDoc(doc(db, "dbMVPlayer", "dbFaq", "items", item.id), { order: newOrderForItem });
             await updateDoc(doc(db, "dbMVPlayer", "dbFaq", "items", targetItem.id), { order: newOrderForTarget });

        } else {
            // Standard swap
            await updateDoc(doc(db, "dbMVPlayer", "dbFaq", "items", item.id), { order: targetOrder });
            await updateDoc(doc(db, "dbMVPlayer", "dbFaq", "items", targetItem.id), { order: itemOrder });
        }
      }
      
      // Small delay to ensure DB propagation before refetching
      await new Promise(resolve => setTimeout(resolve, 200));
      await fetchFaqs();
    } catch (error) {
      console.error("Error moving FAQ:", error);
      alert("Failed to move FAQ");
    } finally {
      setIsSaving(false);
    }
  };



    const startEdit = (item: FAQItem) => {



      setEditingId(item.id);



      setEditForm({ ...item });



      setEditFile(null);



    };



  



        const parseText = (text: string) => {



  



          const parts = text.split(/(\[STRONG\].*?\[\/STRONG\]|\[BOLD\].*?\[\/BOLD\]|\[LINK\].*?\[\/LINK\])/g);



  



          return parts.map((part, i) => {



  



            if (part.startsWith("[STRONG]") && part.endsWith("[/STRONG]")) {



  



              const content = part.slice(8, -9);



  



              return (



  



                <strong key={i} className="text-[#B0D18B] font-bold">



  



                  {content}



  



                </strong>



  



              );



  



            }



  



            if (part.startsWith("[BOLD]") && part.endsWith("[/BOLD]")) {



  



              const content = part.slice(6, -7);



  



              return (



  



                <strong key={i} className="font-bold text-[#E2E3D8]">



  



                  {content}



  



                </strong>



  



              );



  



            }



  



            if (part.startsWith("[LINK]") && part.endsWith("[/LINK]")) {



  



              const content = part.slice(6, -7);



  



              const [label, href] = content.split("|");



  



              return (



  



                <Link key={i} href={href || "#"} className="text-[#B0D18B] hover:text-[#9DC077] underline font-medium">



  



                  {label}



  



                </Link>



  



              );



  



            }



  



            return part;



  



          });



  



        };



  



    const categories = ["General", "Smartwatch", "Padel", "Tennis", "Badminton"] as const;



  return (

    <div className="animate-in fade-in duration-500">

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">

        <div className="text-center mb-12">

          <h1 

            className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4 opacity-0 blur-md translate-y-8"

            style={{

              animation: "fadeSlideUnblur 1s ease-out 0.6s forwards",

            }}

                    >

                      Frequently Asked Questions

                    </h1>

                  </div>

          

                  <div className="flex justify-center flex-wrap gap-4 sm:gap-6 mb-12 opacity-0 blur-md translate-y-4"

             style={{ animation: "fadeSlideUnblur 1s ease-out 0.9s forwards" }}>

            {categories.map(cat => (

              <Link 

                key={cat}

                href={`#${cat.toLowerCase()}`} 

                onClick={(e) => scrollToCategory(e, cat)}

                className="text-xs font-bold uppercase tracking-widest text-[#8E9285] hover:text-[#B0D18B] transition-colors"

              >

                {cat}

              </Link>

            ))}

        </div>

        

        {isAdmin && faqs.length === 0 && !loading && (

           <div className="flex justify-center gap-4 mb-8">

              <button 

                onClick={handleSeedDefaults}

                className="flex items-center gap-2 px-4 py-2 bg-[#2C2E26] text-[#E2E3D8] border border-[#8E9285]/20 rounded-lg font-bold text-sm hover:bg-[#3C3E36] transition-colors"

              >

                <Database className="h-4 w-4" />

                Seed Defaults

              </button>

           </div>

        )}



        {loading ? (

          <div className="flex justify-center py-12">

            <Loader2 className="h-8 w-8 text-[#B0D18B] animate-spin" />

          </div>

        ) : (

          <div className="max-w-3xl mx-auto space-y-6">

            {categories.filter(cat => faqs.some(f => f.category === cat)).map((cat, index, array) => {

              const catFaqs = faqs.filter(f => f.category === cat);
              const isLastCategory = index === array.length - 1;

              return (

                <div key={cat} className={!isLastCategory ? 'mb-12 sm:mb-24' : ''}>

                  {catFaqs.map((faq, idx) => (

                    <div key={faq.id} id={idx === 0 ? cat.toLowerCase() : undefined} className={idx === 0 ? "scroll-mt-32" : ""}>

                      <div 

                        className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 mb-6 opacity-0 blur-md translate-y-8 hover:border-[#B0D18B] transition-colors duration-200 relative shadow-[0_0_30px_rgba(176,209,139,0.05)]"

                        style={{

                          animation: `fadeSlideUnblur 1s ease-out ${1.0 + (index * 0.2) + (idx * 0.1)}s forwards`,

                        }}

                      >

                        {/* Show category tag only for first item */}

                        {idx === 0 && (

                          <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">

                            {cat}

                          </div>

                        )}

                        

                        {/* Edit Mode */}

                        {editingId === faq.id ? (

                          <div className="space-y-4">

                             <input 

                                className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg p-2 text-[#E2E3D8] font-sriracha outline-none focus:border-[#B0D18B]"

                                value={editForm.question || ""}

                                onChange={(e) => setEditForm({...editForm, question: e.target.value})}

                                disabled={isSaving}

                             />

                             <textarea 

                                className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg p-2 text-[#C4C8BA] text-sm outline-none focus:border-[#B0D18B] min-h-[100px]"

                                value={editForm.answer || ""}

                                onChange={(e) => setEditForm({...editForm, answer: e.target.value})}

                                disabled={isSaving}

                             />

                             

                             {/* Image Input for Edit */}

                             <div className="flex items-center gap-3">

                                <label className={`flex items-center gap-2 cursor-pointer bg-[#B0D18B]/10 hover:bg-[#B0D18B]/20 px-3 py-2 rounded-lg text-[#B0D18B] text-xs font-bold ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}>

                                   <ImageIcon className="h-4 w-4" />

                                   {editFile ? "Change Image" : (editForm.imageUrl ? "Replace Image" : "Add Image")}

                                   <input 

                                      type="file" 

                                      className="hidden" 

                                      accept="image/*"

                                      onChange={(e) => {

                                         if (e.target.files?.[0]) setEditFile(e.target.files[0]);

                                      }}

                                      disabled={isSaving}

                                   />

                                </label>

                                {(editFile || editForm.imageUrl) && (

                                   <button 

                                      onClick={() => {

                                         setEditFile(null);

                                         setEditForm({...editForm, imageUrl: null as any}); // Mark for deletion

                                      }}

                                      className="text-red-400 hover:text-red-300 text-xs"

                                      disabled={isSaving}

                                   >

                                      Remove Image

                                   </button>

                                )}

                                {editFile && <span className="text-xs text-[#8E9285]">{editFile.name}</span>}

                             </div>



                             <div className="flex gap-2 justify-end">

                                <button 

                                   onClick={() => { setEditingId(null); setEditFile(null); }}

                                   className="p-2 text-[#8E9285] hover:text-[#E2E3D8]"

                                   title="Cancel"

                                   disabled={isSaving}

                                >

                                   <X className="h-4 w-4" />

                                </button>

                                <button 

                                   onClick={() => handleUpdate(faq.id, faq)}

                                   className="p-2 bg-[#B0D18B]/10 text-[#B0D18B] rounded-lg hover:bg-[#B0D18B]/20 flex items-center justify-center min-w-[32px]"

                                   title="Save"

                                   disabled={isSaving}

                                >

                                   {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}

                                </button>

                             </div>

                          </div>

                        ) : (

                          /* View Mode */

                          <>

                             <div className="flex justify-between items-start gap-4">

                                <h3 className="text-[#E2E3D8] font-semibold font-sriracha mb-2 text-lg">

                                   {faq.question}

                                </h3>

                                {isAdmin && (

                                   <div className="flex gap-2 shrink-0">

                                      <button 

                                         onClick={() => startInsertAfter(faq)}

                                         className="p-1.5 text-[#8E9285] hover:text-[#B0D18B] transition-colors"

                                         title="Insert Q&A Below"

                                         disabled={isSaving}

                                      >

                                         <Plus className="h-3.5 w-3.5" />

                                      </button>

                                      <button 
                                         onClick={() => handleMove(faq, 'up')}
                                         className={`p-1.5 text-[#8E9285] hover:text-[#B0D18B] transition-colors ${idx === 0 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                         title="Move Up"
                                         disabled={isSaving || idx === 0}
                                      >
                                         <ArrowUp className="h-3.5 w-3.5" />
                                      </button>

                                      <button 
                                         onClick={() => handleMove(faq, 'down')}
                                         className={`p-1.5 text-[#8E9285] hover:text-[#B0D18B] transition-colors ${idx === catFaqs.length - 1 ? 'opacity-30 cursor-not-allowed' : ''}`}
                                         title="Move Down"
                                         disabled={isSaving || idx === catFaqs.length - 1}
                                      >
                                         <ArrowDown className="h-3.5 w-3.5" />
                                      </button>

                                                                            <button 

                                                                               onClick={() => startEdit(faq)}

                                                                               className="p-1.5 text-[#8E9285] hover:text-[#B0D18B] transition-colors"

                                                                               title="Edit"

                                                                               disabled={isSaving}

                                                                            >

                                                                               <Edit2 className="h-3.5 w-3.5" />

                                                                            </button>

                                                                            {idx !== 0 && (

                                                                               <button 

                                                                                  onClick={() => handleDelete(faq)}

                                                                                  className="p-1.5 text-[#8E9285] hover:text-red-400 transition-colors"

                                                                                  title="Delete"

                                                                                  disabled={isSaving}

                                                                               >

                                                                                  {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}

                                                                               </button>

                                                                            )}

                                                                         </div>

                                                                      )}

                             </div>

                                                          <p className="text-[#C4C8BA] text-sm leading-relaxed whitespace-pre-wrap mb-4">

                                                             {parseText(faq.answer)}

                                                          </p>

                                                                                       {faq.imageUrl && (

                                                                                          <div className="flex justify-center mt-2">

                                                                                             <img 

                                                                                                src={faq.imageUrl} 

                                                                                                alt="FAQ Image" 

                                                                                                className="rounded-lg max-h-64 object-cover"

                                                                                             />

                                                                                          </div>

                                                                                       )}

                          </>

                        )}

                      </div>



                      {/* Inline Insert Form */}

                      {insertingAfterId === faq.id && (

                        <div className="bg-[#1A1C16]/50 border border-[#B0D18B]/50 rounded-2xl p-6 mb-6 ml-6 relative shadow-[0_0_30px_rgba(176,209,139,0.1)]">

                           <h4 className="text-[#B0D18B] text-xs font-bold uppercase mb-4">New Q&A (Category: {faq.category})</h4>

                           <div className="space-y-4">

                             <input 

                                className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg p-2 text-[#E2E3D8] outline-none focus:border-[#B0D18B]"

                                value={addForm.question || ""}

                                onChange={(e) => setAddForm({...addForm, question: e.target.value})}

                                placeholder="Enter question..."

                                autoFocus

                                disabled={isSaving}

                             />

                             <textarea 

                                className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg p-2 text-[#E2E3D8] outline-none focus:border-[#B0D18B] min-h-[100px]"

                                value={addForm.answer || ""}

                                onChange={(e) => setAddForm({...addForm, answer: e.target.value})}

                                placeholder="Enter answer..."

                                disabled={isSaving}

                             />

                             

                             {/* Image Input for Insert */}

                             <div className="flex items-center gap-3">

                                <label className={`flex items-center gap-2 cursor-pointer bg-[#B0D18B]/10 hover:bg-[#B0D18B]/20 px-3 py-2 rounded-lg text-[#B0D18B] text-xs font-bold ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}>

                                   <ImageIcon className="h-4 w-4" />

                                   {addFile ? "Change Image" : "Add Image"}

                                   <input 

                                      type="file" 

                                      className="hidden" 

                                      accept="image/*"

                                      onChange={(e) => {

                                         if (e.target.files?.[0]) setAddFile(e.target.files[0]);

                                      }}

                                      disabled={isSaving}

                                   />

                                </label>

                                {addFile && (

                                   <div className="flex items-center gap-2">

                                      <span className="text-xs text-[#8E9285]">{addFile.name}</span>

                                      <button 

                                         onClick={() => setAddFile(null)} 

                                         className="text-red-400 hover:text-red-300 text-xs"

                                         disabled={isSaving}

                                      >

                                         <X className="h-3 w-3" />

                                      </button>

                                   </div>

                                )}

                             </div>



                             <div className="flex gap-2 justify-end">

                                <button 

                                   onClick={() => { setInsertingAfterId(null); setAddFile(null); }}

                                   className="px-4 py-2 text-[#8E9285] hover:text-[#E2E3D8] font-bold text-sm"

                                   disabled={isSaving}

                                >

                                   Cancel

                                </button>

                                <button 

                                   onClick={() => handleInsertSubmit(faq)}

                                   className="px-4 py-2 bg-[#B0D18B] text-[#1E3702] rounded-lg font-bold text-sm hover:bg-[#9DC077] flex items-center gap-2 min-w-[70px] justify-center"

                                   disabled={isSaving}

                                >

                                   {isSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}

                                </button>

                             </div>

                           </div>

                        </div>

                      )}

                    </div>

                  ))}

                </div>

              );

            })}



            {faqs.length === 0 && (

               <div className="text-center py-12 text-[#8E9285] italic opacity-0 blur-md translate-y-8" style={{ animation: "fadeSlideUnblur 1s ease-out 1.0s forwards" }}>

                  No FAQs found. {isAdmin && "Click 'Seed Defaults' to populate."}

               </div>

            )}

          </div>

        )}

      </section>

    </div>

  );

}